import { createContext, useContext, useState } from "react";
import axios from "axios";
import { Home, Key, Utensils, Briefcase, HelpCircle, CheckCircle, ArrowRight, ArrowLeft, Send } from "lucide-react";
import { PageHero } from "../components/Shared";
import { PhotoReportSection } from "../components/PhotoReportSection";
import { useScrollToSuccess } from "../lib/hooks";
import { PHONE, PHONE_LINK, FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";
import { trackLead } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  { value: "Residential Home", Icon: Home },
  { value: "Airbnb / Vacation Rental", Icon: Key },
  { value: "Restaurant", Icon: Utensils },
  { value: "Office / Commercial", Icon: Briefcase },
  { value: "Other", Icon: HelpCircle },
];

const BEDROOMS = ["1", "2", "3", "4", "5+"];
const BATHROOMS = ["1", "2", "3", "4+"];
const SIZES = ["Under 1,000 sq ft", "1,000–2,000 sq ft", "2,000–3,000 sq ft", "Over 3,000 sq ft"];
const FREQUENCIES = ["One-time", "Weekly", "Bi-weekly", "Monthly"];
const EXTRAS = ["Deep cleaning", "Inside fridge & oven", "Interior windows", "Laundry & linens", "Move-in / Move-out", "Post-construction"];

export const ESTIMATE_COPY_EN = {
  lang: "en",
  hero: {
    badge: "Free Estimate",
    title: <>Build Your Free <span style={{ color: GOLD }}>Estimate</span></>,
    subtitle: "Answer 4 quick questions and receive a personalized estimate within 24 hours — no commitment, no hidden fees.",
  },
  steps: ["What do you need cleaned?", "Tell us about the space", "How often & any extras?", "Where do we send your estimate?"],
  stepOf: (n) => `Step ${n} of 4`,
  groups: {
    size: "Approximate size",
    bedrooms: "Bedrooms (if applicable)",
    bathrooms: "Bathrooms (if applicable)",
    frequency: "Cleaning frequency",
    extras: "Extras (optional — select all that apply)",
  },
  placeholders: { name: "Full Name *", phone: "Phone Number *", email: "Email Address *", notes: "Anything else we should know? (optional)" },
  buttons: { back: "Back", next: "Continue", submit: "Get My Estimate", sending: "Sending..." },
  success: {
    title: "Estimate Request Received!",
    body: (name) => <>Thank you, <strong>{name}</strong>! Our team is preparing your personalized estimate — you'll hear from us within <strong>24 hours</strong>.</>,
    call: `Need it sooner? Call ${PHONE}`,
  },
  error: `Something went wrong. Please try again or call us at ${PHONE}.`,
  options: {},
};

const CopyContext = createContext(ESTIMATE_COPY_EN);
const useCopy = () => useContext(CopyContext);
const useLabel = () => {
  const { options } = useCopy();
  return (value) => options[value] || value;
};

const OptionPill = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className="px-4 py-2.5 text-sm font-semibold border-2 transition-all duration-200"
    style={{
      borderColor: selected ? NAVY : "rgba(27,58,107,0.2)",
      background: selected ? NAVY : "#FFFFFF",
      color: selected ? "#FFFFFF" : "#2D2D2D",
      fontFamily: FONT_BODY,
    }}
  >
    {children}
  </button>
);

const FieldGroup = ({ label, children }) => (
  <div className="mb-6">
    <div className="text-sm font-bold mb-3" style={{ color: NAVY, fontFamily: FONT_HEAD }}>
      {label}
    </div>
    <div className="flex flex-wrap gap-2.5">{children}</div>
  </div>
);

const StepProperty = ({ form, update }) => {
  const label = useLabel();
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {PROPERTY_TYPES.map(({ value, Icon }) => {
        const selected = form.propertyType === value;
        return (
          <button
            key={value}
            type="button"
            data-testid={`property-option-${value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => update("propertyType", value)}
            className="p-5 border-2 flex flex-col items-center gap-3 transition-all duration-200 hover:-translate-y-0.5"
            style={{
              borderColor: selected ? GOLD : "rgba(27,58,107,0.15)",
              background: selected ? "rgba(212,168,67,0.1)" : "#FFFFFF",
            }}
          >
            <Icon size={26} style={{ color: selected ? GOLD : NAVY }} />
            <span className="text-sm font-semibold text-center" style={{ color: "#2D2D2D", fontFamily: FONT_BODY }}>
              {label(value)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const StepDetails = ({ form, update }) => {
  const { groups } = useCopy();
  const label = useLabel();
  return (
    <div>
      <FieldGroup label={groups.size}>
        {SIZES.map((s) => (
          <OptionPill key={s} selected={form.squareFeet === s} onClick={() => update("squareFeet", s)} testid={`size-${SIZES.indexOf(s)}`}>
            {label(s)}
          </OptionPill>
        ))}
      </FieldGroup>
      <FieldGroup label={groups.bedrooms}>
        {BEDROOMS.map((b) => (
          <OptionPill key={b} selected={form.bedrooms === b} onClick={() => update("bedrooms", b)} testid={`bedrooms-${b.replace("+", "plus")}`}>
            {b}
          </OptionPill>
        ))}
      </FieldGroup>
      <FieldGroup label={groups.bathrooms}>
        {BATHROOMS.map((b) => (
          <OptionPill key={b} selected={form.bathrooms === b} onClick={() => update("bathrooms", b)} testid={`bathrooms-${b.replace("+", "plus")}`}>
            {b}
          </OptionPill>
        ))}
      </FieldGroup>
    </div>
  );
};

const StepFrequency = ({ form, update, toggleExtra }) => {
  const { groups } = useCopy();
  const label = useLabel();
  return (
    <div>
      <FieldGroup label={groups.frequency}>
        {FREQUENCIES.map((f) => (
          <OptionPill key={f} selected={form.frequency === f} onClick={() => update("frequency", f)} testid={`frequency-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {label(f)}
          </OptionPill>
        ))}
      </FieldGroup>
      <FieldGroup label={groups.extras}>
        {EXTRAS.map((x) => (
          <OptionPill key={x} selected={form.extras.includes(x)} onClick={() => toggleExtra(x)} testid={`extra-${EXTRAS.indexOf(x)}`}>
            {label(x)}
          </OptionPill>
        ))}
      </FieldGroup>
    </div>
  );
};

const INPUT_STYLE = {
  fontFamily: FONT_BODY,
  color: "#2D2D2D",
  border: "1px solid rgba(27,58,107,0.22)",
  borderRadius: "2px",
  fontSize: "14px",
};

const StepContact = ({ form, update }) => {
  const { placeholders } = useCopy();
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <input
          type="text"
          placeholder={placeholders.name}
          value={form.fullName}
          onChange={(e) => update("fullName", e.target.value)}
          data-testid="estimate-input-name"
          className="w-full px-4 py-3"
          style={INPUT_STYLE}
        />
        <input
          type="tel"
          placeholder={placeholders.phone}
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          data-testid="estimate-input-phone"
          className="w-full px-4 py-3"
          style={INPUT_STYLE}
        />
      </div>
      <input
        type="email"
        placeholder={placeholders.email}
        value={form.email}
        onChange={(e) => update("email", e.target.value)}
        data-testid="estimate-input-email"
        className="w-full px-4 py-3"
        style={INPUT_STYLE}
      />
      <textarea
        placeholder={placeholders.notes}
        value={form.notes}
        onChange={(e) => update("notes", e.target.value)}
        data-testid="estimate-input-notes"
        rows={3}
        className="w-full px-4 py-3 resize-none"
        style={INPUT_STYLE}
      />
    </div>
  );
};

const SuccessScreen = ({ name }) => {
  const { success } = useCopy();
  return (
    <div className="text-center py-8" data-testid="estimate-success">
      <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(212,168,67,0.18)" }}>
        <CheckCircle size={40} style={{ color: GOLD }} />
      </div>
      <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
        {success.title}
      </h2>
      <p className="text-base mb-6 leading-relaxed max-w-md mx-auto" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
        {success.body(name)}
      </p>
      <a href={PHONE_LINK} className="text-sm font-bold" style={{ color: NAVY, fontFamily: FONT_BODY }}>
        {success.call}
      </a>
    </div>
  );
};

const useEstimateWizard = (copy) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    propertyType: "",
    squareFeet: "",
    bedrooms: "",
    bathrooms: "",
    frequency: "",
    extras: [],
    fullName: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleExtra = (x) =>
    setForm((prev) => ({
      ...prev,
      extras: prev.extras.includes(x) ? prev.extras.filter((e) => e !== x) : [...prev.extras, x],
    }));

  const canContinue = [
    !!form.propertyType,
    !!form.squareFeet,
    !!form.frequency,
    !!(form.fullName && form.phone && form.email),
  ][step];

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/estimate`, { ...form, language: copy.lang });
      trackLead({ form_name: "estimate", page_language: copy.lang });
      setSubmitted(true);
    } catch {
      setError(copy.error);
    } finally {
      setLoading(false);
    }
  };

  return { step, setStep, form, update, toggleExtra, canContinue, submit, loading, submitted, error };
};

const StepProgress = ({ step }) => {
  const { steps, stepOf } = useCopy();
  return (
    <>
      <div className="flex items-center gap-2 mb-8">
        {steps.map((t, i) => (
          <div key={t} className="flex-1 h-1.5 rounded-full" style={{ background: i <= step ? GOLD : "rgba(27,58,107,0.1)" }} />
        ))}
      </div>
      <h2 className="text-xl md:text-2xl font-bold mb-2" style={{ fontFamily: FONT_HEAD, color: NAVY }} data-testid="estimate-step-title">
        {steps[step]}
      </h2>
      <p className="text-sm mb-8" style={{ color: "#9CA3AF", fontFamily: FONT_BODY }}>
        {stepOf(step + 1)}
      </p>
    </>
  );
};

const WizardControls = ({ step, setStep, canContinue, submit, loading }) => {
  const { buttons } = useCopy();
  return (
    <div className="flex items-center justify-between mt-10">
      {step > 0 ? (
        <button
          type="button"
          data-testid="estimate-back-btn"
          onClick={() => setStep(step - 1)}
          className="flex items-center gap-2 px-5 py-3 text-sm font-bold border-2 transition-all hover:bg-gray-50"
          style={{ borderColor: "rgba(27,58,107,0.25)", color: NAVY, fontFamily: FONT_BODY }}
        >
          <ArrowLeft size={15} />
          {buttons.back}
        </button>
      ) : (
        <span />
      )}
      {step < 3 ? (
        <button
          type="button"
          data-testid="estimate-next-btn"
          onClick={() => setStep(step + 1)}
          disabled={!canContinue}
          className="flex items-center gap-2 px-7 py-3 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-40"
          style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
        >
          {buttons.next}
          <ArrowRight size={15} />
        </button>
      ) : (
        <button
          type="button"
          data-testid="estimate-submit-btn"
          onClick={submit}
          disabled={!canContinue || loading}
          className="flex items-center gap-2 px-7 py-3 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-40"
          style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
        >
          {loading ? buttons.sending : (<><Send size={15} /> {buttons.submit}</>)}
        </button>
      )}
    </div>
  );
};

const WizardStep = ({ step, form, update, toggleExtra }) => {
  if (step === 0) return <StepProperty form={form} update={update} />;
  if (step === 1) return <StepDetails form={form} update={update} />;
  if (step === 2) return <StepFrequency form={form} update={update} toggleExtra={toggleExtra} />;
  return <StepContact form={form} update={update} />;
};

export default function EstimatePage({ copy = ESTIMATE_COPY_EN }) {
  const { step, setStep, form, update, toggleExtra, canContinue, submit, loading, submitted, error } = useEstimateWizard(copy);
  useScrollToSuccess(submitted, '[data-testid="estimate-success"]', '[data-testid="estimate-page"]');

  return (
    <CopyContext.Provider value={copy}>
      <PageHero
        testid="estimate-page-hero"
        badge={copy.hero.badge}
        title={copy.hero.title}
        subtitle={copy.hero.subtitle}
      />
      <PhotoReportSection lang={copy.lang} testid="estimate-photo-report" />
      <section data-testid="estimate-page" className="py-14 md:py-20" style={{ background: "#F9FAFB" }} lang={copy.lang}>
        <div className="max-w-2xl mx-auto px-4 md:px-8">
          <div className="bg-white p-6 md:p-10 shadow-sm" style={{ border: "1px solid rgba(27,58,107,0.08)" }}>
            {submitted ? (
              <SuccessScreen name={form.fullName} />
            ) : (
              <>
                <StepProgress step={step} />
                <WizardStep step={step} form={form} update={update} toggleExtra={toggleExtra} />
                {error && (
                  <div data-testid="estimate-error" className="mt-6 px-4 py-3 text-sm" style={{ background: "rgba(239,68,68,0.07)", color: "#DC2626", border: "1px solid rgba(239,68,68,0.2)", fontFamily: FONT_BODY }}>
                    {error}
                  </div>
                )}
                <WizardControls step={step} setStep={setStep} canContinue={canContinue} submit={submit} loading={loading} />
              </>
            )}
          </div>
        </div>
      </section>
    </CopyContext.Provider>
  );
}
